// ============================================================================
// PDF EXPORT UTILITY - Generate downloadable PDF reports
// ============================================================================

// Using jsPDF library - add to package.json: "jspdf": "^2.5.1"
// npm install jspdf

export const generateReportPDF = async (results, userAnswers) => {
  // Dynamically import jsPDF
  const { jsPDF } = await import('jspdf');
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = margin;
  
  // Helper functions
  const checkNewPage = (neededSpace = 30) => {
    if (yPos + neededSpace > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };
  
  const getRiskColor = (level) => {
    const colors = {
      'CRITICAL': [220, 38, 38],
      'HIGH': [234, 88, 12],
      'MODERATE': [202, 138, 4],
      'LOW': [22, 163, 74],
      'MINIMAL': [6, 182, 212]
    };
    return colors[level] || [100, 100, 100];
  };

  // ========== PAGE 1: COVER ==========
  
  // Header bar
  doc.setFillColor(17, 24, 39);
  doc.rect(0, 0, pageWidth, 50, 'F');
  
  // Red accent line
  doc.setFillColor(239, 68, 68);
  doc.rect(0, 50, pageWidth, 4, 'F');
  
  // Logo text
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('Lab', margin, 30);
  doc.setTextColor(239, 68, 68);
  doc.text('2040', margin + 22, 30);
  
  // Date
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric' 
  });
  doc.setFontSize(10);
  doc.setTextColor(156, 163, 175);
  doc.text(today, pageWidth - margin - 45, 30);
  
  yPos = 80;
  
  // Title
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('AI DISRUPTION', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 12;
  doc.text('RISK ASSESSMENT', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 15;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(107, 114, 128);
  doc.text('Personalized Report', pageWidth / 2, yPos, { align: 'center' });
  
  // Score Box
  yPos += 25;
  const boxWidth = 100;
  const boxX = (pageWidth - boxWidth) / 2;
  
  doc.setFillColor(...getRiskColor(results.riskLevel));
  doc.roundedRect(boxX, yPos, boxWidth, 55, 5, 5, 'F');
  
  // Score number
  doc.setFontSize(42);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(String(results.compositeScore), pageWidth / 2, yPos + 32, { align: 'center' });
  
  // Risk Level label
  doc.setFontSize(11);
  doc.text(`${results.riskLevel} RISK`, pageWidth / 2, yPos + 46, { align: 'center' });
  
  // Timeline
  yPos += 75;
  doc.setFontSize(11);
  doc.setTextColor(107, 114, 128);
  doc.text(`Expected Impact: ${results.timeHorizon || 'Within 3-5 years'}`, pageWidth / 2, yPos, { align: 'center' });
  
  // ========== PAGE 2: SCORE BREAKDOWN ==========
  doc.addPage();
  yPos = margin;
  
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('Score Breakdown', margin, yPos);
  
  yPos += 8;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 15;
  
  // Metrics
  const metrics = [
    { label: 'Industry Risk', value: results.industryScore || 0, desc: 'How vulnerable your industry is to AI disruption' },
    { label: 'Task Exposure', value: results.riskScore || 0, desc: 'How automatable your daily tasks are' },
    { label: 'AI Knowledge', value: results.knowledgeScore || 0, desc: 'Your current understanding of AI tools', inverted: true },
    { label: 'Readiness Score', value: results.readinessScore || 0, desc: 'How prepared you are to adapt', inverted: true }
  ];
  
  metrics.forEach((metric) => {
    // Label and value
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(17, 24, 39);
    doc.text(metric.label, margin, yPos);
    doc.text(`${metric.value}/100`, pageWidth - margin, yPos, { align: 'right' });
    
    yPos += 8;
    
    // Progress bar background
    doc.setFillColor(229, 231, 235);
    doc.roundedRect(margin, yPos, pageWidth - margin * 2, 8, 2, 2, 'F');
    
    // Progress bar fill
    const barColor = metric.inverted 
      ? (metric.value > 60 ? [34, 197, 94] : metric.value > 30 ? [234, 179, 8] : [239, 68, 68])
      : (metric.value > 60 ? [239, 68, 68] : metric.value > 30 ? [234, 179, 8] : [34, 197, 94]);
    doc.setFillColor(...barColor);
    doc.roundedRect(margin, yPos, (pageWidth - margin * 2) * (metric.value / 100), 8, 2, 2, 'F');
    
    yPos += 12;
    
    // Description
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text(metric.desc, margin, yPos);
    
    yPos += 18;
  });
  
  // ========== KEY RISK FACTORS ==========
  yPos += 5;
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(185, 28, 28);
  doc.text('Key Risk Factors', margin, yPos);
  yPos += 8;
  
  const riskFactors = results.keyRiskFactors || [
    'Industry facing significant AI disruption',
    'High task automation potential',
    'Limited current AI tool proficiency'
  ];
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(55, 65, 81);
  riskFactors.forEach((factor) => {
    doc.text(`• ${factor}`, margin + 5, yPos);
    yPos += 8;
  });
  
  // ========== PROTECTIVE FACTORS ==========
  yPos += 8;
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(21, 128, 61);
  doc.text('Protective Factors', margin, yPos);
  yPos += 8;
  
  const protectiveFactors = results.protectiveFactors || [
    'Willingness to learn and adapt',
    'Unique human-centric skills',
    'Industry experience and relationships'
  ];
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(55, 65, 81);
  protectiveFactors.forEach((factor) => {
    doc.text(`• ${factor}`, margin + 5, yPos);
    yPos += 8;
  });
  
  // ========== PAGE 3: RECOMMENDATIONS ==========
  doc.addPage();
  yPos = margin;
  
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('Personalized Recommendations', margin, yPos);
  
  yPos += 8;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 15;
  
  const recommendations = results.recommendations || [
    'Start using AI tools daily - begin with ChatGPT or Claude for work tasks',
    'Take the "AI For Everyone" course on Coursera (free to audit)',
    'Identify 3 repetitive tasks you can automate with AI this month',
    'Follow AI thought leaders in your industry on LinkedIn',
    'Build a portfolio showcasing your AI-enhanced work',
    'Network with AI-forward professionals in your field'
  ];
  
  recommendations.forEach((rec, index) => {
    checkNewPage(25);
    
    // Number circle
    doc.setFillColor(99, 102, 241);
    doc.circle(margin + 6, yPos - 2, 6, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(String(index + 1), margin + 4, yPos);
    
    // Recommendation text
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(55, 65, 81);
    const lines = doc.splitTextToSize(rec, pageWidth - margin * 2 - 20);
    doc.text(lines, margin + 18, yPos);
    
    yPos += (lines.length * 6) + 12;
  });
  
  // ========== 90-DAY ACTION PLAN SUMMARY ==========
  yPos += 10;
  checkNewPage(80);
  
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('Your 90-Day Action Plan', margin, yPos);
  yPos += 12;
  
  const phases = [
    { title: 'Phase 1: Foundation', weeks: 'Weeks 1-4', focus: 'AI literacy, tool exploration, industry research, personal skill audit' },
    { title: 'Phase 2: Skill Building', weeks: 'Weeks 5-8', focus: 'Prompt engineering mastery, productivity tools, industry-specific skills, portfolio building' },
    { title: 'Phase 3: Application', weeks: 'Weeks 9-12', focus: 'Workplace integration, advanced specialization, networking, future-proofing strategy' }
  ];
  
  phases.forEach((phase) => {
    checkNewPage(30);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(99, 102, 241);
    doc.text(phase.title, margin, yPos);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text(phase.weeks, pageWidth - margin, yPos, { align: 'right' });
    
    yPos += 6;
    doc.setTextColor(55, 65, 81);
    const focusLines = doc.splitTextToSize(phase.focus, pageWidth - margin * 2);
    doc.text(focusLines, margin, yPos);
    
    yPos += (focusLines.length * 5) + 12;
  });
  
  // CTA
  yPos += 10;
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, 25, 3, 3, 'F');
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('Access your full interactive 90-Day Action Plan at:', margin + 5, yPos + 10);
  
  doc.setTextColor(99, 102, 241);
  doc.text('lab2040.com/action-plan', margin + 5, yPos + 18);
  
  // ========== FOOTER ON ALL PAGES ==========
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Footer line
    doc.setDrawColor(229, 231, 235);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    
    // Footer text
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(156, 163, 175);
    doc.text('Lab2040 AI Disruption Risk Assessment', margin, pageHeight - 8);
    doc.text('www.lab2040.com', pageWidth / 2, pageHeight - 8, { align: 'center' });
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  }
  
  // Save the PDF
  const fileName = `Lab2040_Report_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
  
  return fileName;
};

export default generateReportPDF;
